import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { ShieldCheck, CopyPlus, PencilLine, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader.jsx';
import EmptyState from '@/pages/Guias/components/EmptyState.jsx';
import {
  SECURITY_ROLE_CLONE_MUTATION,
  SECURITY_ROLE_DELETE_MUTATION,
  SECURITY_ROLE_TOGGLE_MUTATION,
  SECURITY_ROLES_QUERY,
} from './gql.js';

function CriticityBadge({ value }) {
  const styles = {
    critica: 'bg-[rgba(220,38,38,0.12)] text-red-600 dark:text-red-300',
    alta: 'bg-[rgba(245,158,11,0.16)] text-amber-600 dark:text-amber-300',
    media: 'bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]',
    baja: 'bg-[rgba(var(--muted),0.12)] text-[rgb(var(--muted))]',
  };

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${styles[value] || styles.media}`}>
      {value || 'media'}
    </span>
  );
}

export default function RolesPermisosList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [levelFilter, setLevelFilter] = useState('todos');
  const [{ data, fetching, error }] = useQuery({
    query: SECURITY_ROLES_QUERY,
    variables: { search: search || null },
  });
  const [, cloneRole] = useMutation(SECURITY_ROLE_CLONE_MUTATION);
  const [, toggleRole] = useMutation(SECURITY_ROLE_TOGGLE_MUTATION);
  const [, deleteRole] = useMutation(SECURITY_ROLE_DELETE_MUTATION);

  const rows = data?.security_roles || [];
  const filteredRows = rows.filter((role) => {
    const matchStatus = statusFilter === 'todos'
      || (statusFilter === 'activos' && role.activo)
      || (statusFilter === 'inactivos' && !role.activo);
    const matchLevel = levelFilter === 'todos'
      || (levelFilter === 'alto' && role.nivel >= 700)
      || (levelFilter === 'medio' && role.nivel >= 300 && role.nivel < 700)
      || (levelFilter === 'bajo' && role.nivel < 300);
    return matchStatus && matchLevel;
  });
  const summary = useMemo(() => ({
    total: filteredRows.length,
    active: filteredRows.filter((role) => role.activo).length,
    critical: filteredRows.filter((role) => (role.permisos || []).some((permission) => permission.criticality === 'critica')).length,
  }), [filteredRows]);

  const handleClone = async (role) => {
    const suffix = `${role.nombre} copia`;
    await cloneRole({
      id: role.id,
      input: {
        codigo: `${role.codigo || role.nombre}`.toLowerCase().replace(/\s+/g, '_') + '_copia',
        nombre: suffix,
      },
    });
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] pb-16">
      <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-10">
        <PageHeader
          title="Seguridad · Roles"
          subtitle="Modelá puestos operativos reales, jerarquía, scope y permisos por módulo sin depender de reglas dispersas."
          actions={(
            <button
              type="button"
              onClick={() => navigate('/seguridad/roles/nuevo')}
              className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(var(--shadow),0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <Plus className="h-4 w-4" />
              Nuevo rol
            </button>
          )}
        />

        <section className="grid gap-4 lg:grid-cols-[1fr_280px]">
          <div className="card rounded-2xl p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <article className="rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.82)] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[rgb(var(--muted))]">Roles</p>
                <p className="mt-2 text-3xl font-semibold text-[rgb(var(--fg))]">{summary.total}</p>
              </article>
              <article className="rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.82)] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[rgb(var(--muted))]">Activos</p>
                <p className="mt-2 text-3xl font-semibold text-[rgb(var(--fg))]">{summary.active}</p>
              </article>
              <article className="rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.82)] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[rgb(var(--muted))]">Con permisos críticos</p>
                <p className="mt-2 text-3xl font-semibold text-[rgb(var(--fg))]">{summary.critical}</p>
              </article>
            </div>
          </div>

          <aside className="card rounded-2xl p-5">
            <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--muted))]">Reglas</h2>
            <div className="mt-4 space-y-3 text-sm text-[rgba(var(--fg),0.78)]">
              <p>Los roles agrupan permisos operativos, nivel base y alcance por sucursal o tenant.</p>
              <p>La edición impacta de inmediato en todos los usuarios que lo usan.</p>
              <p>Los permisos críticos quedan marcados para endurecer auditoría y jerarquía.</p>
            </div>
          </aside>
        </section>

        <section className="card rounded-2xl p-5">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--muted))]" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por nombre, código o descripción"
                className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.9)] py-3 pl-10 pr-4 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </label>
            <div className="grid gap-3 md:grid-cols-2">
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.9)] px-3 py-3 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                <option value="todos">Todos los estados</option>
                <option value="activos">Activos</option>
                <option value="inactivos">Inactivos</option>
              </select>
              <select value={levelFilter} onChange={(event) => setLevelFilter(event.target.value)} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.9)] px-3 py-3 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                <option value="todos">Todos los niveles</option>
                <option value="alto">Jerarquía alta</option>
                <option value="medio">Jerarquía media</option>
                <option value="bajo">Jerarquía base</option>
              </select>
            </div>
          </div>

          <div className="mt-5 overflow-hidden rounded-2xl border border-[rgba(var(--border),0.45)]">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[rgba(var(--border),0.35)]">
                <thead className="bg-[rgba(var(--subpanel),0.72)]">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-[rgb(var(--muted))]">
                    <th className="px-4 py-3">Rol</th>
                    <th className="px-4 py-3">Nivel</th>
                    <th className="px-4 py-3">Scope</th>
                    <th className="px-4 py-3">Permisos</th>
                    <th className="px-4 py-3">Usuarios</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(var(--border),0.25)] bg-[rgba(var(--panel),0.82)]">
                  {fetching ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-[rgb(var(--muted))]">Cargando roles…</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-red-500">{error.message}</td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8">
                        <EmptyState
                          icon={ShieldCheck}
                          title="Todavía no hay roles cargados"
                          description="Creá perfiles operativos base y después asignalos a usuarios concretos."
                          action={(
                            <button
                              type="button"
                              onClick={() => navigate('/seguridad/roles/nuevo')}
                              className="rounded-xl border border-[rgba(var(--brand),0.45)] px-4 py-2 text-sm font-semibold text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                            >
                              Crear primer rol
                            </button>
                          )}
                        />
                      </td>
                    </tr>
                  ) : filteredRows.map((role) => {
                    const criticality = role.permisos?.some((permission) => permission.criticality === 'critica')
                      ? 'critica'
                      : role.permisos?.some((permission) => permission.criticality === 'alta')
                        ? 'alta'
                        : 'media';

                    return (
                      <tr key={role.id} className="align-top">
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-[rgb(var(--fg))]">{role.nombre}</span>
                              <CriticityBadge value={criticality} />
                            </div>
                            <p className="text-xs text-[rgb(var(--muted))]">{role.codigo || 'sin_código'}</p>
                            <p className="text-sm text-[rgba(var(--fg),0.72)]">{role.descripcion || 'Sin descripción funcional cargada.'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold text-[rgb(var(--fg))]">{role.nivel}</td>
                        <td className="px-4 py-4 text-sm text-[rgba(var(--fg),0.78)]">{role.scopeTipo}</td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-2">
                            {(role.permisos || []).slice(0, 4).map((permission) => (
                              <span key={permission.code} className="rounded-full bg-[rgba(var(--brand),0.12)] px-2.5 py-1 text-xs font-medium text-[rgb(var(--brand))]">
                                {permission.code}
                              </span>
                            ))}
                            {(role.permisos || []).length > 4 ? (
                              <span className="rounded-full bg-[rgba(var(--subpanel),0.8)] px-2.5 py-1 text-xs font-medium text-[rgb(var(--muted))]">
                                +{role.permisos.length - 4}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-[rgba(var(--fg),0.78)]">{role.userCount}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${role.activo ? 'bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--muted),0.12)] text-[rgb(var(--muted))]'}`}>
                            {role.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => navigate(`/seguridad/roles/${role.id}`)}
                              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.65)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                            >
                              <PencilLine className="h-3.5 w-3.5" />
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleClone(role)}
                              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.65)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                            >
                              <CopyPlus className="h-3.5 w-3.5" />
                              Clonar
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleRole({ id: role.id, active: !role.activo })}
                              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.65)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                            >
                              {role.activo ? 'Desactivar' : 'Activar'}
                            </button>
                            {!role.esSistema ? (
                              <button
                                type="button"
                                onClick={() => deleteRole({ id: role.id })}
                                className="inline-flex items-center gap-2 rounded-xl border border-[rgba(220,38,38,0.35)] px-3 py-2 text-xs font-semibold text-red-600 dark:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                              >
                                Baja lógica
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
